<template>
  <div class="w-full">
    <ToastAlert :is-active="toastAlert" :type="typeMsg" :message="msg" @close="closeToast"/>

    <!-- Header con título y botón -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-0 mb-6">
      <h2 class="text-xl font-medium text-black">Asigna un prospecto a la propiedad</h2>
      <button 
        class="flex items-center justify-center sm:justify-start rounded-lg bg-tuin-100 hover:bg-tuin_hover px-5 py-2.5 text-sm text-white font-medium transition-colors w-full sm:w-auto"
        @click="handleAddProspect"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M10 5.5C10.2652 5.5 10.5196 5.60536 10.7071 5.79289C10.8946 5.98043 11 6.23478 11 6.5V9.5H14C14.2652 9.5 14.5196 9.60536 14.7071 9.79289C14.8946 9.98043 15 10.2348 15 10.5C15 10.7652 14.8946 11.0196 14.7071 11.2071C14.5196 11.3946 14.2652 11.5 14 11.5H11V14.5C11 14.7652 10.8946 15.0196 10.7071 15.2071C10.5196 15.3946 10.2652 15.5 10 15.5C9.73478 15.5 9.48043 15.3946 9.29289 15.2071C9.10536 15.0196 9 14.7652 9 14.5V11.5H6C5.73478 11.5 5.48043 11.3946 5.29289 11.2071C5.10536 11.0196 5 10.7652 5 10.5C5 10.2348 5.10536 9.98043 5.29289 9.79289C5.48043 9.60536 5.73478 9.5 6 9.5H9V6.5C9 6.23478 9.10536 5.98043 9.29289 5.79289C9.48043 5.60536 9.73478 5.5 10 5.5Z" fill="white"/>
        </svg>
        <span class="ml-2">Agregar nuevo prospecto</span>
      </button>
    </div>
    
    <!-- Grid de prospectos con scroll vertical -->
    <div class="max-h-[400px] overflow-y-auto">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pb-6">
        <div
          v-for="(item, index) in props.list"
          :key="item.id"
          class="bg-white rounded-lg border w-full h-[145px] border-[#E2E8F0] p-4 shadow-sm hover:shadow-md transition-shadow duration-200 relative"
        >
          <!-- Checkbox en la esquina superior derecha -->
          <div class="absolute top-3 right-3">
            <input
              type="checkbox"
              v-model="item.is_checked"
              @change="addProperty(index)"
              class="w-4 h-4 border border-gray-300 rounded bg-gray-50 text-tuin_base-0 focus:ring-transparent"
            />
          </div>

          <!-- Información del prospecto -->
          <div class="flex items-center gap-3 h-full">
            <!-- Avatar con iniciales -->
            <div class="flex-shrink-0">
              <div class="w-[70px] h-[70px] rounded-full bg-gray-200 flex items-center justify-center">
                <span class="text-[22px] font-semibold text-gray-900">
                  {{ getInitials(item) }}
                </span>
              </div>
            </div>

            <!-- Datos del prospecto -->
            <div class="flex-1 min-w-0 pr-8">
              <!-- Badge de estado -->
              <div class="mb-2">
                <span 
                  v-if="item.status"
                  :class="[
                    'inline-block font-medium text-xs px-2 py-1 rounded-md', 
                    getBadgeBgForId(item.status), 
                    getBadgeTextForId(item.status)
                  ]"
                >
                  {{ getStatusName(item.status) }}
                </span>
              </div>
              <div class="text-base font-medium text-gray-800 truncate mb-1">
                {{ getFullName(item) }}
              </div>
              <div class="text-sm font-medium text-[#475569]">
                Teléfono: <span class="font-bold text-sm text-gray-700">{{ item.country_code || '+52' }} {{ item.phone || 'Sin teléfono' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>    
    </div>

    <!--Paginator-->
    <div
      v-if="props.list.length > 0"
      class="flex w-full md:justify-end justify-center items-center mt-6 mb-4"
    >
      <fwb-pagination
        v-model="localCurrentPage"
        :total-pages="props.totalPages"
        @click="changePage()"
        previous-label="Anterior"
        next-label="Siguiente"
        :slice-length="1"
      >
        <template #page-button="{ disabled, page, setPage }">
          <button
            class="disabled:cursor-not-allowed ml-0 flex h-8 items-center justify-center border bg-white border-gray-300 px-3 leading-tight text-gray-500 first:rounded-l-lg last:rounded-r-lg"
            :class="{
              'text-tuin-100': disabled
            }"
            :disabled="disabled"
            @click="setPage(page)"
          >
            {{ page }}
          </button>
        </template>
      </fwb-pagination>
    </div>
  </div>
</template>

<script setup>
import { watch, computed, ref } from "vue";
import {FwbPagination} from "flowbite-vue";
import ToastAlert from "@/commond/ToastAlert.vue";
import { useStore } from "vuex";
const store = useStore();
let session = computed(() => store.getters.getUserSession);
import axios from "axios";
const props = defineProps({
  list: {
    type: Array,
    required: true,
  },
  propertyId: {
    type: Number,
    required: true,
    default: null,
  },
  currentPage: {
    type: Number,
    default: 1,
  },
  totalPages: {
    type: Number,
    default: 1,
  }
});
let toastAlert = ref(false);
let typeMsg = ref("");
let msg = ref("");

// Variable local para el v-model del paginador
let localCurrentPage = ref(props.currentPage);

// Sincronizar la variable local cuando cambien los props
watch(() => props.currentPage, (newPage) => {
  localCurrentPage.value = newPage;
});

// Definir eventos que se pueden emitir
const emit = defineEmits(['add-prospect', 'change-page']);

// Función para manejar el clic del botón agregar prospecto
const handleAddProspect = () => {
  emit('add-prospect');
};

// Función para manejar cambio de página
const changePage = () => {
  emit('change-page', localCurrentPage.value);
};

watch(
  () => props,
  async (newVal) => {
    console.log("", newVal);
  },
  { immediate: true }
);

// Mapas de colores para badges (basados en KanbanBoard)
const badgeBgMap = {
  '1': 'bg-green-100',
  '2': 'bg-yellow-100',
  '3': 'bg-cyan-100',
  '4': 'bg-violet-100',
  '5': 'bg-orange-100',
  '6': 'bg-red-100',
}

const badgeTextMap = {
  '1': 'text-green-800',
  '2': 'text-yellow-800',
  '3': 'text-cyan-800',
  '4': 'text-violet-800',
  '5': 'text-orange-800',
  '6': 'text-red-800',
}

// Catálogo de estatus para obtener el nombre
const statuses = [
  { name: 'Pendiente de contactar', status: '1' },
  { name: 'En proceso', status: '2' },
  { name: 'Finalizado', status: '3' },
  { name: 'No aplica', status: '4' },
  { name: 'Perdido', status: '5' },
  { name: 'Descartado', status: '6' },
]

// Funciones de utilidad para badges
const getBadgeBgForId = (id) => {
  return badgeBgMap[String(id)] || 'bg-gray-100'
}

const getBadgeTextForId = (id) => {
  return badgeTextMap[String(id)] || 'text-gray-800'
}

const getStatusName = (statusId) => {
  const status = statuses.find(s => String(s.status) === String(statusId))
  return status ? status.name : 'Sin estado'
}

const getFullName = (item) => {
  // Fallback al full_name si existe
  return item.full_name || 'Sin nombre'
}

async function addProperty(pos) {
  console.log("se debe mandar ", props.list[pos]);

  try {
    let url_link = `${process.env.VUE_APP_API_BASE_URL}/broker/lead/${session.value.id}/add-property`;

    let postParams = {
      property_id: props.propertyId,
      lead_id: props.list[pos].id,
      is_checked: props.list[pos].is_checked
    };

    var options = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.value.token_api}`,
      },
    }

    const response = await axios.post(url_link, postParams, options);

    if (response.status == 401) {
      store.dispatch("clearUserSession");
    }

    if (response.status == 200) {
      showToast("success", response.data.message);
    }
  } catch (error) {
    console.error("Error fetching data:", error);
  }
}

// Función para obtener iniciales del prospecto
const getInitials = (item) => {
  const fullName = getFullName(item)
  if (fullName === 'Sin nombre') return 'SN'
  
  const names = fullName.split(' ').filter(Boolean)
  if (names.length >= 2) {
    return (names[0][0] + names[1][0]).toUpperCase()
  } else if (names.length === 1) {
    return names[0].slice(0, 2).toUpperCase()
  }
  return 'SN'
}

async function showToast(type, message) {
  toastAlert.value = true;
  typeMsg.value = type;
  msg.value = message;

  setTimeout(() => {
    closeToast();
  }, 3000);
}

async function closeToast() {
  toastAlert.value = false;
  typeMsg.value = "";
  msg.value = "";
}
</script>
